// `POST /v1/admin/provision` (route `grant.provision`, `apps/api/src/routes/admin.ts`):
// the operator's ONE door for giving a friend their own account (plan 102 slice 05).
// Invite them if they are new, approve them off the waitlist, grant capacity — one
// call, one `admin.provision` audit row. `admin:grants`, on an operator credential
// (`lib/operator-credential.ts`), like every staff write.
//
// The body (`ProvisionBodySchema`, `.strict()`):
//   - `email` (anyone) or `login` (someone who has signed in): who.
//   - no capacity named = the invite bundle (`DEFAULT_INVITE_BUNDLE`, 5 AI devs × 30 d);
//     `ai_devs` names another number instead (`unlimited` is not offered here).
//   - `expires_in_days`: how long the grant lasts — replaces the bundle's 30 d, or
//     lapses an `ai_devs` grant; `null` = ongoing.
//   - `account_id`: fund an org the person OWNS instead of their personal account —
//     sent only when the operator chose it explicitly (decision 6, never inferred).
//   - `reason`: required; recorded on the grant.
//
// The answer says each effect separately (`invitation`, `approval`, `grants`, and the
// `boxes` they imply — the server's number, `null` uncapped); a
// deferred invite (`approval: 'not_yet_registered'`) holds NO grant yet — the capacity
// is booked on the invitation and granted on accept. Every refusal is the server's own
// sentence (400 for a malformed body, 422 for the control's codes, `not_bookable` and
// `not_owner` included), rendered verbatim by the caller (`errorReason`).

import { t } from '../i18n';
import { type OperatorOutcome, withOperatorCredential } from './operator-credential';
import { SESSION_TOKEN } from './session';

export type ProvisionInput = {
  readonly email: string;
  readonly reason: string;
  readonly ai_devs?: number;
  readonly expires_in_days?: number | null;
  readonly account_id?: string;
};

/** One grant the provision holds for the person, as the route summarizes it. */
export type ProvisionGrant = {
  readonly grant_id: string;
  readonly kind: string;
  readonly amount: number | null;
  readonly expires_at_iso: string | null;
};

export type ProvisionAnswer = {
  readonly user_id: string | null;
  readonly login: string | null;
  readonly email: string | null;
  readonly invitation: {
    readonly invitationId: string;
    readonly acceptUrl: string;
    readonly expiresAtIso: string;
    readonly reissued: boolean;
    readonly mailed: boolean;
  } | null;
  readonly approval: 'approved' | 'already_approved' | 'not_yet_registered';
  readonly grants: readonly ProvisionGrant[];
  readonly audit_id: string | null;
  /**
   * The box budget those grants imply, by the server's billing rule (the booking's
   * on a deferred invite); `null` = uncapped. Never computed here.
   */
  readonly boxes: number | null;
};

/** The server's box budget as a sentence: `null` is uncapped, never "0" or "null". */
export function boxBudgetCopy(boxes: number | null): string {
  return boxes === null ? t('admin.boxes_unlimited') : t('admin.access_invited_boxes', { boxes });
}

/** Provision one person. `ok` carries the answer; a door refusal sends nothing. */
export function provisionUser(
  input: ProvisionInput,
  token: string | null = SESSION_TOKEN,
): Promise<OperatorOutcome<ProvisionAnswer>> {
  return withOperatorCredential(
    { scopes: ['admin:grants'], name: 'admin console: invite by email', token },
    (send) =>
      send<ProvisionAnswer>('/v1/admin/provision', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  );
}
